/**
 * The notifications bell: a top-bar button carrying the unread badge that opens
 * the {@link NotificationsPanel} in a dropdown. Self-contained — it owns its
 * data (via {@link useNotifications}), its open/close state and keyboard
 * dismissal — so the shell mounts `<NotificationBell />` with no extra wiring.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { useNotifications } from './useNotifications';
import { NotificationsPanel } from './NotificationsPanel';

import './notifications.css';

export interface NotificationBellProps {
  /** How many recent notifications to load into the panel. */
  limit?: number;
  /** Override navigation (defaults to react-router). */
  onNavigate?: (path: string) => void;
}

export function NotificationBell({ limit = 12, onNavigate }: NotificationBellProps): React.JSX.Element {
  const state = useNotifications(limit);
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();

  const go = useCallback(
    (path: string) => {
      setOpen(false);
      if (onNavigate) onNavigate(path);
      else void navigate(path);
    },
    [navigate, onNavigate],
  );

  // Close on outside click and on Escape while open.
  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent): void => {
      if (rootRef.current !== null && !rootRef.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const badge = state.unread;
  const label =
    badge > 0
      ? `Notifications, ${String(badge)} unread`
      : 'Notifications, none unread';

  return (
    <div className="ntf-bell" ref={rootRef}>
      <button
        type="button"
        className="ntf-bell__btn"
        aria-label={label}
        aria-haspopup="dialog"
        aria-expanded={open}
        onClick={() => {
          setOpen((o) => !o);
        }}
      >
        <span aria-hidden="true" className="ntf-bell__glyph">
          🔔
        </span>
        {badge > 0 ? (
          <span className="ntf-bell__badge" aria-hidden="true">
            {badge > 99 ? '99+' : badge}
          </span>
        ) : null}
      </button>
      {open ? (
        <div className="ntf-bell__pop">
          <NotificationsPanel state={state} onNavigate={go} />
        </div>
      ) : null}
    </div>
  );
}
